import { useState, useEffect, useCallback } from 'react'
import { M365Group, M365DirectoryRole, M365UserGroupsResponse } from '../../types/m365Users'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'

interface GroupsSectionProps {
  userId: string
  customerId: string
  customerTenantId: string
}

export function GroupsSection({
  userId,
  customerId,
  customerTenantId,
}: GroupsSectionProps) {
  const { getCustomerGraphAccessToken } = useAuth()
  const [groups, setGroups] = useState<M365Group[]>([])
  const [directoryRoles, setDirectoryRoles] = useState<M365DirectoryRole[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const token = await getCustomerGraphAccessToken(customerTenantId)
      const response = await api.getM365UserGroups(token, customerId, userId) as M365UserGroupsResponse

      setGroups(response.groups || [])
      setDirectoryRoles(response.directoryRoles || [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load groups'
      if (errorMessage.includes('Insufficient privileges') || errorMessage.includes('Authorization_RequestDenied')) {
        setError('Insufficient permissions to view group memberships. Group.Read.All permission may be required.')
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }, [userId, customerId, customerTenantId, getCustomerGraphAccessToken])

  useEffect(() => {
    fetchGroups()
  }, [fetchGroups])

  const getGroupTypeLabel = (group: M365Group) => {
    if (group.groupTypes.includes('Unified')) {
      return { label: 'Microsoft 365', className: 'bg-purple-100 text-purple-800' }
    }
    if (group.securityEnabled && !group.mailEnabled) {
      return { label: 'Security', className: 'bg-blue-100 text-blue-800' }
    }
    if (group.mailEnabled && !group.securityEnabled) {
      return { label: 'Distribution', className: 'bg-green-100 text-green-800' }
    }
    if (group.mailEnabled && group.securityEnabled) {
      return { label: 'Mail-enabled Security', className: 'bg-yellow-100 text-yellow-800' }
    }
    return { label: 'Group', className: 'bg-gray-100 text-gray-800' }
  }

  if (loading) {
    return (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            <span className="ml-2 text-sm text-gray-500">Loading groups...</span>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Unable to load groups</h3>
                <p className="mt-1 text-sm text-yellow-700">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Directory Roles */}
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
            Directory Roles ({directoryRoles.length})
          </h3>
          {directoryRoles.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No directory roles assigned</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {directoryRoles.map(role => (
                <span
                  key={role.id}
                  className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-red-100 text-red-800"
                  title={role.description}
                >
                  <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  {role.displayName}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Groups */}
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
            Group Memberships ({groups.length})
          </h3>
          {groups.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No group memberships</p>
          ) : (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {groups.map(group => {
                    const typeInfo = getGroupTypeLabel(group)
                    return (
                      <tr key={group.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                              <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">{group.displayName}</div>
                              {group.mail && (
                                <div className="text-xs text-gray-500">{group.mail}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeInfo.className}`}>
                            {typeInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-500 max-w-xs truncate" title={group.description}>
                            {group.description || '-'}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
